import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { UploadSimple, LinkSimple, Check, Megaphone } from "@phosphor-icons/react";

export default function Advertise() {
  const [sp] = useSearchParams();
  const tenantSlug = sp.get("tenant") || null;
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ title: "", target_url: "", image_url: "", placement: "home" });
  const [imageMode, setImageMode] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [purchaseOn, setPurchaseOn] = useState(false);
  useEffect(() => { api.get("/store-settings").then(({ data }) => setPurchaseOn(!!data.ads)).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/ad-tiers", { params: tenantSlug ? { tenant: tenantSlug } : {} }).then((r) => { setTiers(r.data); if (r.data[0]) setSelectedTier(r.data[0].id); });
  }, [tenantSlug]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("image_url", data.url);
    } catch { setErr("Image upload failed."); } finally { setUploading(false); }
  };

  const pay = async () => {
    setErr("");
    if (!form.title || !form.target_url || !form.image_url) { setErr("Please add a headline, link URL, and banner image."); return; }
    if (!selectedTier) { setErr("Please choose a plan."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-ad", { tier_id: selectedTier, origin_url: window.location.origin, tenant_slug: tenantSlug, ad: form });
      window.location.href = data.url;
    } catch (e) { setErr(e.response?.data?.detail || "Could not start checkout."); setBusy(false); }
  };

  const field = "w-full mt-1 bg-zinc-900 border border-zinc-700 focus:border-[#FFD600] outline-none px-3 py-3 text-white";

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Megaphone size={30} weight="fill" color="#FFD600" />
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Advertise <span className="text-[#FFD600]">With Us</span></h1>
      </div>
      <p className="text-zinc-400 mb-8">Buy a rotating banner ad shown across the homepage. Big, bold, and impossible to miss.</p>

      {err && <p className="text-[#FF007F] text-sm mb-4 border border-[#FF007F] p-3" data-testid="advertise-error">{err}</p>}

      <div className="grid gap-4 mb-8">
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Headline *</label>
          <input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="ad-title-input" className={field} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Destination URL *</label>
          <input value={form.target_url} onChange={(e) => upd("target_url", e.target.value)} placeholder="https://…" data-testid="ad-url-input" className={field} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-400">Banner Image *</label>
          <div className="flex gap-2 mt-1 mb-3">
            <button type="button" onClick={() => setImageMode("upload")} className={`flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border ${imageMode === "upload" ? "border-[#FFD600] text-[#FFD600]" : "border-zinc-700 text-zinc-400"}`}><UploadSimple size={14} /> Upload</button>
            <button type="button" onClick={() => setImageMode("url")} className={`flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border ${imageMode === "url" ? "border-[#FFD600] text-[#FFD600]" : "border-zinc-700 text-zinc-400"}`}><LinkSimple size={14} /> Paste URL</button>
          </div>
          {imageMode === "upload" ? (
            <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0])} data-testid="ad-image-file"
              className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#FFD600] file:text-black file:font-bold file:uppercase file:text-xs" />
          ) : (
            <input value={form.image_url} onChange={(e) => upd("image_url", e.target.value)} placeholder="https://image-url…" data-testid="ad-image-url" className={field} />
          )}
          {uploading && <p className="text-xs text-[#FFD600] mt-2">Uploading…</p>}
          {form.image_url && !uploading && <p className="text-xs text-[#39FF14] mt-2 flex items-center gap-1"><Check size={14} /> Banner ready</p>}
        </div>
      </div>

      <h2 className="font-display text-2xl font-black uppercase mb-4">Choose a <span className="text-[#FFD600]">Plan</span></h2>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {tiers.map((t, i) => (
          <button key={t.id} type="button" onClick={() => setSelectedTier(t.id)} data-testid="ad-tier-card"
            className={`relative text-left p-5 border transition-all ${selectedTier === t.id ? "border-[#FFD600] shadow-[6px_6px_0px_#FFD600]" : "border-zinc-700 hover:border-zinc-500"}`}>
            {i === tiers.length - 1 && <span className="absolute top-2 right-2 bg-[#FF007F] text-white text-[10px] font-black uppercase px-2 py-0.5 font-mono">Best Value</span>}
            <p className="text-xs uppercase tracking-widest text-zinc-400">{t.name}</p>
            <p className="font-mono font-extrabold text-3xl text-[#39FF14] mt-1">${t.price}</p>
            <p className="text-sm text-zinc-400 mt-1">{t.days} days live</p>
          </button>
        ))}
      </div>

      {purchaseOn ? (
      <button onClick={pay} disabled={busy} data-testid="ad-pay-button"
        className="w-full sm:w-auto px-8 py-4 bg-[#FFD600] text-black font-black uppercase tracking-widest border border-[#FFD600] hover:bg-[#09090B] hover:text-[#FFD600] transition-colors disabled:opacity-50 hard-shadow">
        {busy ? "Redirecting…" : "Pay & Launch Ad"}
      </button>
      ) : (
      <button disabled data-testid="ad-pay-button"
        className="w-full sm:w-auto px-8 py-4 bg-zinc-700 text-zinc-300 font-black uppercase tracking-widest border border-zinc-600 cursor-not-allowed hard-shadow">
        Coming Soon
      </button>
      )}
      <p className="text-xs text-zinc-500 mt-3">{purchaseOn ? "Secure checkout via Stripe. Cash App Pay & cards accepted." : "🚀 Ad purchasing opens soon! We're finalizing checkout — check back shortly to launch your banner."}</p>
    </div>
  );
}
